/* -----------------------------------------------------------------------------------------------------
  - Created: [28-11-2025] by [SREERAJ]
  - Modified: [xx-xx-xxxx] by [SREERAJ]
  - Description: Status Card with responsive Pie/Doughnut charts + Tooltips + Hover effects
------------------------------------------------------------------------------------------------------- */

import React from "react";
import { Pie, Doughnut } from "react-chartjs-2";
import { Card, CardContent, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "chart.js/auto";
import "./StatusCard.css";
import { hasPrivilege } from "../../../../utils/privileges";

const StatusCard = ({
    uniqueKey,
    title,
    data,
    labels,
    colors,
    children,
    icon,
    showFooter = false,
    total,
    isShowPie = false, // Pie chart enabled
}) => {

    const navigate = useNavigate();
    const privileges = useSelector((state) => state.privileges.privileges);

    // ---------------- NAVIGATION ---------------- //
    const handleViewNavigate = () => {
        const routes = {
            equipment: "/resources/equipment/list",
            employee: "/resources/workforce/list",
            invoice: "/reports/reports/invoice-report",
            schedules: "/worksites/schedule/overview"
        };
        navigate(routes[uniqueKey]);
    };

    // ----------- CHART DATA ----------- //
    const chartData = {
        labels,
        datasets: [
            {
                data,
                backgroundColor: colors,
                borderWidth: 0,
                spacing: 0,
            },
        ],
    };

    // ----------- CHART OPTIONS (USER FRIENDLY) ----------- //
    const chartOptions = {
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: "#000",
                padding: 10,
                titleFont: { size: 14, weight: "600" },
                bodyFont: { size: 13 },
                displayColors: true,
            },
        },
    };

    const isAllZero = data.every((x) => Number(x) === 0);

    // ----------- CHECK PERMISSIONS ----------- //
    const showViewAll =
        (uniqueKey === "equipment" && hasPrivilege(privileges, "Resources", 5, 2)) ||
        (uniqueKey === "employee" && hasPrivilege(privileges, "Resources", 6, 2)) ||
        (uniqueKey === "invoice" && hasPrivilege(privileges, "Reports", 27, 2)) ||
        (uniqueKey === "schedules" && hasPrivilege(privileges, "Work site", 3, 2));

    return (
        <Card variant="outlined" sx={{ borderRadius: "16px", height: "100%" }}>
            <CardContent>

                {/* HEADER */}
                <Box className="statusCard_header">
                    <div className="statusCard_titleBlock">
                        <span className="statusCard_icon">{icon}</span>
                        <div className="statusCard_titleRow">
                            <span className="statusCard_title">{title}</span>
                            <span className="statusCard_totalBadge">{total}</span>
                        </div>
                    </div>

                    {showViewAll && (
                        <span className="statusCard_viewAll" onClick={handleViewNavigate}>
                            View All
                        </span>
                    )}
                </Box>

                {/* CONTENT */}
                <Box className="statusCard_contentWrapper">
                    <Box className="statusCard_chartBox">

                        {isAllZero ? (
                            <span className="statusCard_noData">No data available</span>
                        ) : (
                            isShowPie ? (
                                <Pie data={chartData} options={chartOptions} />
                            ) : (
                                <Doughnut
                                    data={chartData}
                                    options={{
                                        ...chartOptions,
                                        cutout: "65%", // nicer doughnut look
                                    }}
                                />
                            )
                        )}

                    </Box>

                    {!showFooter && (
                        <Box className="statusCard_rightSection">
                            {children}
                        </Box>
                    )}
                </Box>

                {showFooter && (
                    <div className="statusCard_footer">{children}</div>
                )}

            </CardContent>
        </Card>
    );
};

export default StatusCard;
